import * as XLSX from 'xlsx';
import {
    GetAnecdotesPage as DLGetAnecdotesPage,
    GetAllAnecdotes as DLGetAllAnecdotes,
    GetNewerAnecdotes as DLGetNewerAnecdotes,
    ToggleAnecdoteVisibility as DLToggleVisibility,
    ToggleAnecdoteShown as DLToggleShown,
    DeleteAnecdote as DLDeleteAnecdote,
    ResetAnecdotes as DLResetAnecdotes,
    SelectAnecdote as DLSelectAnecdote,
    DeselectAnecdote as DLDeselectAnecdote,
    SetAnecdoteMode as DLSetAnecdoteMode,
    SetAnecdotePosition as DLSetAnecdotePosition
} from './../../../DL/Admin/Overlays/Anecdotes.js';

/**
 * ============================================================================
 * BUSINESS LOGIC: GESTIÓN DE ANÉCDOTAS (ADMIN / OVERLAYS)
 * ============================================================================
 */

/**
 * Formatea una fecha ISO a cadena legible en español mexicano: DD/MM/AAAA HH:mm:ss hrs
 */
const formatFullDate = (dateString) => {
    if (!dateString) return "Fecha no disponible";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Fecha no disponible";
    const formatted = date.toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    return `${formatted} hrs`;
};

/**
 * Sanitiza valores de texto para evitar ejecución de fórmulas en hojas de cálculo.
 * Antepone un apóstrofe (') si la cadena inicia con '=', '+', '-', o '@'.
 */
const escapeFormula = (val) => {
    if (typeof val !== 'string') return val;
    if (/^\s*[=+\-@|]/.test(val)) {
        return "'" + val;
    }
    return val;
};

/**
 * Obtiene una página de anécdotas validando parámetros.
 */
const GetAnecdotesPage = async ({ limit = 30, cursor = null, sort = 'DESC' } = {}) => {
    try {
        const safeLimit = Math.max(1, Math.min(Number(limit) || 30, 100));
        const safeSort = sort === 'ASC' ? 'ASC' : 'DESC';

        const response = await DLGetAnecdotesPage({
            limit: safeLimit,
            cursor,
            sort: safeSort
        });

        if (response.success) {
            const rawItems = response.data.items || [];
            const safeItems = rawItems.map(item => ({
                id: item.id,
                content: item.content || '',
                sender: item.sender || 'Anónimo',
                createdAtUtc: item.createdAtUtc,
                isHidden: Boolean(item.isHidden),
                wasShown: Boolean(item.wasShown),
                isSelected: Boolean(item.isSelected)
            }));

            return {
                success: true,
                data: {
                    items: safeItems,
                    nextCursor: response.data.nextCursor ?? null,
                    hasMore: Boolean(response.data.hasMore),
                    total: Number(response.data.total) || safeItems.length
                }
            };
        } else {
            return { success: false, message: response.message };
        }
    } catch (error) {
        return { success: false, message: 'Error al procesar el listado de anécdotas.' };
    }
};

/**
 * Obtiene todas las anécdotas sin paginación para reportes.
 */
const GetAllAnecdotes = async () => {
    try {
        const response = await DLGetAllAnecdotes();
        if (response.success) {
            const rawItems = Array.isArray(response.data) ? response.data : [];
            const safeItems = rawItems.map(item => ({
                id: item.id,
                content: item.content || '',
                sender: item.sender || 'Anónimo',
                createdAtUtc: item.createdAtUtc,
                isHidden: Boolean(item.isHidden),
                wasShown: Boolean(item.wasShown),
                isSelected: Boolean(item.isSelected)
            }));
            return { success: true, data: safeItems };
        }
        return { success: false, message: response.message };
    } catch (error) {
        return { success: false, message: 'Error al obtener todas las anécdotas.' };
    }
};

/**
 * Obtiene anécdotas recientes.
 */
const GetNewerAnecdotes = async (lastId) => {
    try {
        const safeId = Math.max(0, Number(lastId) || 0);
        const response = await DLGetNewerAnecdotes(safeId);
        if (response.success) {
            const rawItems = Array.isArray(response.data) ? response.data : [];
            const safeItems = rawItems.map(item => ({
                id: item.id,
                content: item.content || '',
                sender: item.sender || 'Anónimo',
                createdAtUtc: item.createdAtUtc,
                isHidden: Boolean(item.isHidden),
                wasShown: Boolean(item.wasShown),
                isSelected: Boolean(item.isSelected)
            }));
            return { success: true, data: safeItems };
        }
        return { success: false, message: response.message };
    } catch (error) {
        return { success: false, message: 'Error al buscar nuevas anécdotas.' };
    }
};

const ToggleAnecdoteVisibility = async (id) => {
    try {
        return await DLToggleVisibility(id);
    } catch {
        return { success: false, message: 'Error al cambiar visibilidad de la anécdota.' };
    }
};

const ToggleAnecdoteShown = async (id) => {
    try {
        return await DLToggleShown(id);
    } catch {
        return { success: false, message: 'Error al cambiar estado visto de la anécdota.' };
    }
};

const DeleteAnecdote = async (id) => {
    try {
        return await DLDeleteAnecdote(id);
    } catch {
        return { success: false, message: 'Error al eliminar la anécdota.' };
    }
};

const ResetAnecdotes = async () => {
    try {
        return await DLResetAnecdotes();
    } catch {
        return { success: false, message: 'Error al reiniciar la dinámica de anécdotas.' };
    }
};

const SelectAnecdote = async (id, mode = 'fullscreen') => {
    try {
        return await DLSelectAnecdote(id, mode);
    } catch {
        return { success: false, message: 'Error al proyectar la anécdota.' };
    }
};

const DeselectAnecdote = async () => {
    try {
        return await DLDeselectAnecdote();
    } catch {
        return { success: false, message: 'Error al quitar la anécdota de pantalla.' };
    }
};

const SetAnecdoteMode = async (mode) => {
    try {
        return await DLSetAnecdoteMode(mode);
    } catch {
        return { success: false, message: 'Error al configurar el modo de visualización.' };
    }
};

const SetAnecdotePosition = async (offset) => {
    try {
        return await DLSetAnecdotePosition(offset);
    } catch {
        return { success: false, message: 'Error al ajustar la posición del cintillo.' };
    }
};

/**
 * Genera y descarga el reporte oficial en formato Excel (.xlsx) con escape de fórmulas.
 */
const DownloadExcelReport = (anecdotesList) => {
    try {
        if (!anecdotesList || anecdotesList.length === 0) {
            return { success: false, message: 'No hay datos para exportar.' };
        }

        const rows = anecdotesList.map((item, index) => ({
            'No.': index + 1,
            'ID': item.id,
            'Anécdota': escapeFormula(item.content || ''),
            'Remitente': escapeFormula(item.sender || 'Anónimo'),
            'Fecha de Envío': formatFullDate(item.createdAtUtc),
            'Visto en Pantalla': item.wasShown ? 'SÍ' : 'NO',
            'Oculto / Moderado': item.isHidden ? 'SÍ' : 'NO'
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(rows);

        // Anchos de columna óptimos
        if (worksheet) {
            worksheet['!cols'] = [
                { wch: 6 },   // No.
                { wch: 8 },   // ID
                { wch: 50 },  // Anécdota
                { wch: 25 },  // Remitente
                { wch: 24 },  // Fecha
                { wch: 18 },  // Visto
                { wch: 18 }   // Oculto
            ];
        }

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Tu Mejor Anécdota');

        const now = new Date();
        const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const fileName = `Reporte_Tu_Mejor_Anecdota_ADSL_${dateStr}.xlsx`;

        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error("Error al generar Excel:", error);
        return { success: false, message: 'Error al generar el archivo Excel.' };
    }
};

export {
    formatFullDate,
    escapeFormula,
    GetAnecdotesPage,
    GetAllAnecdotes,
    GetNewerAnecdotes,
    ToggleAnecdoteVisibility,
    ToggleAnecdoteShown,
    DeleteAnecdote,
    ResetAnecdotes,
    SelectAnecdote,
    DeselectAnecdote,
    SetAnecdoteMode,
    SetAnecdotePosition,
    DownloadExcelReport
};
